/// <amd-module name="Wit/BoardsHub/KanbanBoard/Components/BoardRow" />
import * as React from "react";
import { useService } from "VSS/Platform/Layout";
import { Icon, IconSize } from "VSSUI/Icon";
import { Observer } from "VSSUI/Observer";
import { ObservableValue } from "VSS/Core/Observable";
import { Tooltip } from "VSSUI/TooltipEx";
import { css } from "VSSUI/Util";
import { isDark, parseColor} from "VSSUI/Utilities/Color";
import { BoardColumnLayout, BoardRowLayout } from "Wit/BoardsHub/Common/KanbanBoardContracts";
import { BoardColumn, IBoardColumnProps, SplitBoardColumn } from "Wit/BoardsHub/KanbanBoard/Components/BoardColumn";
import { DragMode, IPosition, ScrollableData } from "Wit/Common/Controls/Boards/Draggable";
import { IKanbanBoardService } from "../IKanbanBoardService";
import { ClickEditField } from "./ClickEditField";

export interface IBoardRowProps extends Pick<IBoardColumnProps, "cardLayout" | "onCardKeyDown" | "filteredIds" | "workItemTypeColorAndIconsMap"> {
    className?: string;
    rowLayout: BoardRowLayout;
    showRow?: boolean;
    onCardDrag: (e: PointerEvent, draggingElement: HTMLElement, mode: DragMode, position: IPosition) => undefined;
    onSaveToggle: (expanded: boolean, rowId: string) => void;
    columnChanged: ObservableValue<boolean>;
    doneChanged?: ObservableValue<boolean>;
    rowChanged?: ObservableValue<boolean>;
    scrollableData?: ScrollableData;
    onCardFocus?: (columnLayout: BoardColumnLayout, rowName?: string) => void;
}

export const BoardRow: React.FunctionComponent<IBoardRowProps> = ({ className, filteredIds, rowLayout, showRow, onSaveToggle, scrollableData, ...columnProps }) => {
    return (
        <Observer expanded={rowLayout.expanded}>
            {() => {
                const expanded = rowLayout.expanded.value;
                const columns = (
                    <>
                        {rowLayout.columns.map(column =>
                            column.isSplit ? (
                                <SplitBoardColumn
                                    columnLayout={column}
                                    rowName={rowLayout.name}
                                    filteredIds={filteredIds}
                                    scrollableData={scrollableData}
                                    key={`${rowLayout.id}-${column.id}-${column.isDone ? "-done" : "-doing"}`}
                                    {...columnProps}
                                />
                            ) : (
                                <BoardColumn
                                    columnLayout={column}
                                    rowName={rowLayout.name}
                                    filteredIds={filteredIds}
                                    scrollableData={scrollableData}
                                    key={`${rowLayout.id}-${column.id}`}
                                    {...columnProps}
                                />
                            )
                        )}
                    </>
                );

                if (showRow === false) {
                    return columns;
                }

                return (
                    <div className={css("flex-column kanban-board-row", className, expanded && "expanded")} data-row={rowLayout.id}>
                        {showRow && (
                            <BoardRowHeader
                                expanded={expanded}
                                filteredIds={filteredIds}
                                onToggleExpanded={() => {
                                    rowLayout.expanded.value = !rowLayout.expanded.value;
                                    onSaveToggle(rowLayout.expanded.value, rowLayout.id);
                                }}
                                rowLayout={rowLayout}
                            />
                        )}
                        <div className={css("flex-row kanban-board-column-container", className, expanded ? "margin-top-4" : "board-row-collapsed")}>
                            {columns}
                        </div>
                    </div>
                );
            }}
        </Observer>
    );
};

export interface IBoardRowHeaderProps extends Pick<IBoardRowProps, "filteredIds" | "rowLayout"> {
    expanded: boolean;
    onToggleExpanded(): void;
}

export const BoardRowHeader: React.FunctionComponent<IBoardRowHeaderProps> = ({ expanded, filteredIds, onToggleExpanded, rowLayout }) => {
    // Use a map so we can aggregate split column work item counts
    const columnLayoutAndCountMap = new Map<string, { columnLayout: BoardColumnLayout; columnWorkItemCount: number }>();
    for (const columnLayout of rowLayout.columns) {
        const columnWorkItemCount = filteredIds
            ? columnLayout.workItems.value.filter(item => filteredIds.has(item.value.id)).length
            : columnLayout.workItems.length;

        const columnData = columnLayoutAndCountMap.get(columnLayout.id);
        if (columnData) {
            columnData.columnWorkItemCount += columnWorkItemCount;
        } else {
            columnLayoutAndCountMap.set(columnLayout.id, {
                columnLayout,
                columnWorkItemCount
            });
        }
    }

    const columnCountDisplayData = Array.from(columnLayoutAndCountMap.values()).filter(
        ({ columnLayout, columnWorkItemCount }) => columnWorkItemCount > 0
    );

    const memorizedFontColor = React.useMemo(() => {
        const parsedColor = parseColor(rowLayout.color);
        return parsedColor && (isDark(parsedColor) ? "dark" : "light");
    }, [rowLayout.color])

    return (
        <div
            className={css(
                "kanban-board-row-header focusable-board-item flex-row flex-center margin-right-4 padding-vertical-4 padding-horizontal-8 rhythm-horizontal-16",
                rowLayout.isDefault && "default-row",
                memorizedFontColor
            )}
            style={{
                backgroundColor: rowLayout.color ?? ""
            }}
            onClick={onToggleExpanded}
            onKeyDown={event => {
                if (event.key === "Enter") {
                    onToggleExpanded();
                }
            }}
            tabIndex={0}
        >
            <span className="board-row-name flex-row flex-grow text-ellipsis">
                <Icon className="font-weight-semibold icon-margin" iconName={expanded ? "ChevronDown" : "ChevronUp"} size={IconSize.small} />
                <BoardRowName id={rowLayout.id} name={rowLayout.name} />
            </span>
            {!expanded && (
                <span className="flex-row rhythm-horizontal-16">
                    {columnCountDisplayData.map(({ columnLayout, columnWorkItemCount }) => {
                        return (
                            <span key={`row-${rowLayout.id}-column-${columnLayout.id}-count`} className="rhythm-horizontal-4">
                                <span className="font-weight-semibold">{columnWorkItemCount}</span>
                                <Tooltip overflowOnly>
                                    <span className="text-ellipsis">{columnLayout.name}</span>
                                </Tooltip>
                            </span>
                        );
                    })}
                </span>
            )}
        </div>
    );
};

function BoardRowName({ name, id }: { name: string; id: string }) {
    const boardService = useService<IKanbanBoardService>("IKanbanBoardService");

    return (
        <Tooltip overflowOnly>
            <ClickEditField
                inactiveClassName="text-ellipsis"
                value={name}
                onCommit={newName => boardService.renameRow(id, newName)}
                error={boardService.getRowNameError(id)}
            />
        </Tooltip>
    );
}
